<template>
  <th class="data-grid__tableHeading" :class="{ draggable: canUpdate }">
    <div class="heading__content">
      <icon v-if="options.displayColumn === cellKey" name="bookmark" scale=".8" />
      <span
        :contenteditable="
          editableColumnName === `column-name-${gridId}-${widgetKey}-${cellKey}`
        "
        :ref="`column-name-${gridId}-${widgetKey}-${cellKey}`"
        class="data-grid__columnNameVal"
        @click="editColumnName(`column-name-${gridId}-${widgetKey}-${cellKey}`)"
        @blur="
          saveColumnNameVal(`column-name-${gridId}-${widgetKey}-${cellKey}`)
        "
        @keypress.enter.prevent="
          blurEditableColumnNameVal(
            `column-name-${gridId}-${widgetKey}-${cellKey}`
          )
        "
        @keydown.esc="
          resetColumnNameEdit(`column-name-${gridId}-${widgetKey}-${cellKey}`)
        "
      >{{ displayName }}</span>
      <b-dropdown variant="link" no-caret size="sm" v-if="canUpdate" v-b-tooltip title="Actions">
        <template slot="button-content">
          <icon name="ellipsis-v" scale=".8" style="vertical-align: -1px;" />
        </template>
        <b-dropdown-item
          href="#"
          @click.prevent="
            editColumnName(`column-name-${gridId}-${widgetKey}-${cellKey}`)
          "
        >
          Rename
          <span
            @click.stop
            v-if="currentTemplate && dgInst && dgInst.inheritedOptions"
            v-back-to-default="[
              dgInst.inheritedOptions.meta[cellKey],
              options.meta[cellKey],
              'displayName',
              backToDefaultColumnName,
            ]"
          />
        </b-dropdown-item>
        <div v-if="options.meta[cellKey]">
          <b-dropdown-item
            href="#"
            v-if="cellKey !== 'name' && !options.meta[cellKey].inherited"
            @click.prevent="removeColumn"
          >Remove</b-dropdown-item>
        </div>
        <b-dropdown-item href="#" @click.prevent="setAsDisplayColumn">
          Set as display column
          <span
            @click.stop
            v-if="currentTemplate && dgInst && dgInst.inheritedOptions"
            v-back-to-default="[
              dgInst.inheritedOptions,
              options,
              'displayColumn',
              backToDefaultDisplayColumn,
            ]"
          />
        </b-dropdown-item>
        <b-dropdown-item
          href="#"
          v-if="type !== 'files' && type !== 'custom'"
          @click.prevent="showOnChangeHandlerModal"
        >
          On change handler
          <span
            @click.stop
            v-if="currentTemplate && dgInst && dgInst.inheritedOptions"
            v-back-to-default="[
              dgInst.inheritedOptions.meta[cellKey],
              options.meta[cellKey],
              'onChange',
              backToDefault,
            ]"
          />
        </b-dropdown-item>
        <b-dropdown-item
          v-if="type === 'lookup' || type === 'multi_lookup'"
          href="#"
          @click.prevent="showLookUpFilter"
        >LookUp filter</b-dropdown-item>
        <b-dropdown-item
          href="#"
          v-if="type === 'string' || type === 'number'"
          @click.prevent="togglePriceFormatter"
        >
          <div v-if="options.meta[cellKey]">
            <span v-if="options.meta[cellKey].priceFormat">Turn off price format</span>
            <span v-else>Turn on price format</span>
          </div>
          <span
            @click.stop
            v-if="currentTemplate && dgInst && dgInst.inheritedOptions"
            v-back-to-default="[
              dgInst.inheritedOptions.meta[cellKey],
              options.meta[cellKey],
              'priceFormat',
              backToDefault,
            ]"
          />
        </b-dropdown-item>
        <b-dropdown-item href="#" @click="showColWidthModal">
          Minimal width
          <span
            @click.stop
            v-if="currentTemplate && dgInst && dgInst.inheritedOptions"
            v-back-to-default="[
              dgInst.inheritedOptions.meta[cellKey],
              options.meta[cellKey],
              'minWidth',
              backToDefault,
            ]"
          />
        </b-dropdown-item>
        <b-dropdown-item
          v-if="isAbleToChangeColumnType"
          href="#"
          @click.prevent="showChangeColumnTypeModal(cellKey)"
        >Change type</b-dropdown-item>
        <b-dropdown-item v-if="type === 'string'" href="#" @click.prevent="setDefaultSort('asc')">
          <b-badge v-if="isSortApplied('asc')" pill variant="primary" class="menu-selected-marker">
            <icon name="check" />
          </b-badge>
          <span>Sort by asc</span>
        </b-dropdown-item>
        <b-dropdown-item v-if="type === 'string'" href="#" @click.prevent="setDefaultSort('desc')">
          <b-badge v-if="isSortApplied('desc')" pill variant="primary" class="menu-selected-marker">
            <icon name="check" />
          </b-badge>
          <span>Sort by desc</span>
        </b-dropdown-item>
      </b-dropdown>
      <span v-if="isSortable">
        <a
          href="#"
          v-b-tooltip
          title="Sort"
          v-if="sortCol !== this.cellKey"
          @click.prevent="applySort('asc')"
        >
          <icon name="sort" scale=".8" />
        </a>
        <a
          href="#"
          v-if="sortCol === this.cellKey && sortDirection === 'asc'"
          @click.prevent="applySort('desc')"
        >
          <icon name="sort-asc" scale=".8" />
        </a>
        <a
          href="#"
          v-if="sortCol === this.cellKey && sortDirection === 'desc'"
          @click.prevent="applySort('asc')"
        >
          <icon name="sort-desc" scale=".8" />
        </a>
      </span>
    </div>
    <div
      v-if="(filterOn || options.filterVisible) && !filterBuilder"
      class="dg-filterField"
      :class="{ _valueExists: typeof filter[cellKey] !== 'undefined' }"
    >
      <StringFilter
        v-if="type === 'string'"
        :filter="filter"
        :cellKey="cellKey"
        class="data-grid__filterItem"
      />
      <NumberFilter
        v-if="type === 'number'"
        :filter="filter"
        :cellKey="cellKey"
        class="data-grid__filterItem"
      />
      <MultilineTextFilter
        v-if="type === 'multiline_text'"
        :filter="filter"
        :cellKey="cellKey"
        class="data-grid__filterItem"
      />
      <LookUpFilter
        v-if="type === 'lookup' || type === 'multi_lookup'"
        :filter="filter"
        :options="options"
        :cellKey="cellKey"
        :gridData="gridData"
        class="data-grid__filterItem"
      />
      <CheckboxFilter v-if="type === 'checkbox'" :filter="filter" :cellKey="cellKey" />
      <DateFilter
        v-if="type === 'date'"
        :filter="filter"
        :cellKey="cellKey"
        class="data-grid__filterItem"
      />
      <UserFilter
        v-if="type === 'user'"
        :filter="filter"
        :cellKey="cellKey"
        class="data-grid__filterItem"
      />
      <SelectFilter
        v-if="type === 'select'"
        :filter="filter"
        :cellKey="cellKey"
        :options="options"
        class="data-grid__filterItem"
      />
      <a
        v-if="typeof filter[cellKey] !== 'undefined'"
        href="#"
        @click.prevent="removeFromFilter"
        class="dg-filterField__removeItem"
        v-b-tooltip
        title="Remove from Filter"
      >
        <icon name="remove" />
      </a>
    </div>
  </th>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
// services
import db from '@/services/db'
// helpers
import eventBus from '@/helpers/event-bus'
// components
import StringFilter from '../controls/filters/String'
import MultilineTextFilter from '../controls/filters/MultilineText'
import LookUpFilter from '../controls/filters/LookUp'
import CheckboxFilter from '../controls/filters/Checkbox'
import DateFilter from '../controls/filters/Date'
import UserFilter from '../controls/filters/User'
import SelectFilter from '../controls/filters/Select'
import NumberFilter from '../controls/filters/Number'

export default {
  props: [
    'displayName',
    'type',
    'cellKey',
    'gridId',
    'widgetKey',
    'options',
    'sortCol',
    'sortDirection',
    'filterOn',
    'filter',
    'gridData',
    'filterBuilder',
  ],
  components: {
    StringFilter,
    MultilineTextFilter,
    LookUpFilter,
    CheckboxFilter,
    DateFilter,
    UserFilter,
    SelectFilter,
    NumberFilter,
  },
  data() {
    return {
      editableColumnName: null,
      lastKnownColumnNameVal: '',
      resetColumnNameEditFlag: false,
      dgInst: null,
    }
  },
  computed: {
    ...mapState(['currentDash', 'currentTemplate']),
    isAbleToChangeColumnType() {
      switch (this.type) {
        case 'string':
          return true
        case 'lookup':
          return true
        default:
          return false
      }
    },
    isSortable() {
      switch (this.type) {
        case 'files':
          return false
        case 'multi_lookup':
          return false
        case 'custom':
          return false
        case 'profile_image':
          return false
      }
      return true
    },
    isAbleToBeDisplayColumn() {
      switch (this.type) {
        case 'text':
          return true
        case 'multiline_text':
          return true
        case 'checkbox':
          return true
        default:
          return false
      }
    },
    canUpdate() {
      return this.$can('update', 'Dashboard_' + this.currentDash)
    },
  },
  methods: {
    editColumnName(key) {
      this.editableColumnName = key
      this.lastKnownColumnNameVal = this.$refs[key].textContent
      this.$nextTick(() => {
        this.$refs[key].focus()
        this.$cursorToEnd(this.$refs[key])
      })
    },
    blurEditableColumnNameVal(key) {
      this.$refs[key].blur()
    },
    saveColumnNameVal(key) {
      if (this.resetColumnNameEditFlag) {
        this.resetColumnNameEditFlag = false
        return
      }

      const newVal = this.$refs[key].textContent.trim()

      this.editableCell = null
      this.lastKnownCellVal = ''

      if (!newVal) {
        this.resetColumnNameEdit(key)
        return
      }
      if (newVal === this.lastKnownColumnNameVal) return

      db.dataGrid.data
        .saveColumnNameVal(
          this.gridId,
          this.cellKey,
          newVal,
          this.currentTemplate
        )
        .then(() => {
          this.editableColumnName = null
        })

      this.$nextTick(() => {
        this.$store.commit('dataGrid_setLogsValue', {
          type: 'ColChange',
          oldVal: this.cellKey,
          newVal: newVal,
        })
      })
    },
    resetColumnNameEdit(key) {
      this.resetColumnNameEditFlag = true
      this.$refs[key].blur()
      this.$refs[key].textContent = this.lastKnownColumnNameVal.trim()
      this.lastKnownColumnNameVal = ''
      this.editableColumnName = null
    },
    removeColumn() {
      db.dataGrid.data.removeColumn(this.gridId, this.cellKey).then(() => {
        this.$nextTick(() => {
          this.$store.commit('dataGrid_setLogsValue', {
            type: 'ColChange',
            oldVal: this.cellKey,
            order: this.options.meta[this.cellKey].order,
          })
        })
      })
    },
    setAsDisplayColumn() {
      db.dataGrid.data.setAsDisplayColumn(this.gridId, this.cellKey)
    },
    applySort(sortDirection = 'asc') {
      this.$emit('onSort', this.cellKey, sortDirection)
    },
    setDefaultSort(sortDirection) {
      const data = {
        gridId: this.gridId,
        oName: `defaultSort`,
        oValue: {
          column: this.cellKey,
          direction: sortDirection,
        },
      }

      this.$emit('onSort', this.cellKey, sortDirection)

      db.dataGrid.options.saveOption(data)
    },
    removeFromFilter() {
      this.$emit('filterItemRemove', this.cellKey)
    },
    showOnChangeHandlerModal() {
      eventBus.$emit(
        `showOnChangeHandlerModal${this.widgetKey}-${this.gridId}`,
        this.cellKey
      )
    },
    showLookUpFilter() {
      eventBus.$emit(
        `showLookUpFilter${this.widgetKey}-${this.gridId}`,
        this.cellKey
      )
    },
    showChangeColumnTypeModal(cellKey) {
      eventBus.$emit('showChangeColumnTypeModal', cellKey)
    },
    togglePriceFormatter() {
      let val = null

      if (!this.options.meta[this.cellKey].priceFormat) val = true

      const data = {
        gridId: this.gridId,
        oName: `meta/${this.cellKey}/priceFormat`,
        oValue: val,
      }

      db.dataGrid.options.saveOption(data)
    },
    showColWidthModal() {
      eventBus.$emit(
        `showColWidthModal${this.widgetKey}-${this.gridId}`,
        this.cellKey
      )
    },
    backToDefaultColumnName(prop, value) {
      db.dataGrid.data
        .saveColumnNameVal(
          this.gridId,
          this.cellKey,
          value,
          this.currentTemplate
        )
        .then(() => {
          this.editableColumnName = null
        })
    },
    backToDefaultDisplayColumn(prop, value) {
      const data = {
        gridId: this.gridId,
        oName: `displayColumn`,
        oValue: null,
      }

      db.dataGrid.options.saveOption(data)
    },
    backToDefault(prop, value) {
      const data = {
        gridId: this.gridId,
        oName: `meta/${this.cellKey}/${prop}`,
        oValue: typeof value === 'undefined' ? null : value,
      }

      db.dataGrid.options.saveOption(data)
    },
    isSortApplied(direction) {
      return (
        this.options.defaultSort &&
        this.options.defaultSort.column === this.cellKey &&
        this.options.defaultSort.direction === direction
      )
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.dgInst = this.$instByClass(this, 'data-grid') || {}
    })
  },
}
</script>

<style lang="scss">
.data-grid {
  font-family: Open Sans, sans-serif;
  color: #525f7f;
  font-weight: 400;

  &__filterItem {
    min-width: 150px;
  }
}

.data-grid .data-grid {
  &__columnNameVal {
    color: #8898aa;
    text-transform: uppercase;
    font-size: 0.65rem;
  }

  &__tableHeading {
    position: relative;
    text-transform: none;
    font-size: 14px;
    vertical-align: top;
    padding: {
      top: 10px;
      bottom: 10px;
    }

    button {
      padding: 0;

      svg {
        vertical-align: -2px;
      }
    }

    .dropdown-toggle:after {
      display: none;
    }

    .heading__content a > svg,
    .heading__content button > svg {
      color: #8898aa;
    }
  }
}

.dg-filterField {
  position: relative;
  padding-top: 10px;

  &._valueExists {
    padding-right: 30px;
  }

  &__removeItem {
    position: absolute;
    right: 4px;
    top: 17px;
  }
}
</style>
